import type { MockServer } from './server'
import { randBoolean } from '@ngneat/falso'

export function routes(server: MockServer) {
  server.get('/ui_configs/clients/:config_id', function (schema, request) {
    const mergedSettings = schema.first('mergedSettings')
    const clientTitleAvailable =
      mergedSettings?.attrs?.client_title_available !== false

    const hidden = ['country_region', 'einvoice_address', 'einvoice_operator']
    const visible = [
      'firstname',
      'lastname',
      'home_department',
      'secondary_owner_first_name',
      'secondary_owner_last_name',
      'organization_name',
      'customer_type',
      'street_address',
      'street_address_2',
      'street_address_3',
      'zip_code',
      'city',
      'country',
      'email',
      'phone',
      'alt_emails',
      'id_number',
      'critical_notes',
      'remarks',
      'tags',
      'vat_number',
      'register_number',
      'referring_organization',
      'referring_vet',
      'parent_referring_organization',
      'breeder',
      'production_animal_client',
      'farm_code',
      'holding_place_numbers',
      'invoicing_client',
      'no_sms',
      'no_email',
      'on_stop',
      'archived',
      'state',
      'gender',
      'date_of_birth',
      'drivers_licence_number',
      'status_type',
      'due_date_delay',
      // Communication Preferences
      'communication_preference_valid_from',
      'communication_preference_consent',
      'communication_preference_privacy_policy_version',
      'communication_preference_1',
      'communication_preference_2',
    ]

    const disabled = [
      'communication_preference_3', // Non-editable row
    ]

    if (clientTitleAvailable) {
      visible.push('title')
    } else {
      hidden.push('title')
    }

    return {
      data: {
        type: 'UIConfig',
        id: request.params.config_id,
        attributes: {
          resource_type: 'UIConfigPostClient',
          resource_required: false,
          resource_id: 0,
          config: {
            id: request.params.config_id,
            forms: [
              {
                id: request.params.config_id,
                hidden,
                visible,
                required: randBoolean()
                  ? ['lastname', 'home_department']
                  : ['lastname'],
                disabled,
                labels: [
                  { name: 'firstname', label: 'First name' },
                  { name: 'lastname', label: 'Last name' },
                  {
                    name: 'secondary_owner_first_name',
                    label: 'Secondary owner first name',
                  },
                  {
                    name: 'secondary_owner_last_name',
                    label: 'Secondary owner last name',
                  },
                  { name: 'home_department', label: 'Home department' },
                  { name: 'title', label: 'Title' },
                  { name: 'organization_name', label: 'Organisation name' },
                  { name: 'customer_type', label: 'Customer type' },
                  { name: 'street_address', label: 'Street address' },
                  { name: 'street_address_2', label: 'Second line' },
                  { name: 'street_address_3', label: 'Third line' },
                  { name: 'zip_code', label: 'Post code' },
                  { name: 'city', label: 'City' },
                  { name: 'country', label: 'Country' },
                  { name: 'state', label: 'State' },
                  { name: 'country_region', label: 'Province' },
                  { name: 'email', label: 'Email' },
                  { name: 'phone', label: 'Phone number' },
                  { name: 'alt_emails', label: 'Additional emails' },
                  { name: 'id_number', label: 'ID number' },
                  { name: 'critical_notes', label: 'Critical notes' },
                  { name: 'remarks', label: 'Remarks' },
                  { name: 'tags', label: 'Tags' },
                  { name: 'vat_number', label: 'VAT number' },
                  { name: 'register_number', label: 'Register number' },
                  { name: 'due_date_delay', label: 'Due date delay' },
                  { name: 'status_type', label: 'Client type' },
                  { name: 'gender', label: 'Gender' },
                  { name: 'date_of_birth', label: 'Date of birth' },
                  {
                    name: 'drivers_licence_number',
                    label: "Driver's license number",
                  },
                  {
                    name: 'production_animal_client',
                    label: 'Production animal client',
                  },
                  { name: 'farm_code', label: 'Unique farm code' },
                  {
                    name: 'holding_place_numbers',
                    label: 'Holding place number',
                  },
                  {
                    name: 'einvoice_address',
                    label: 'E-invoice electronic address',
                  },
                  {
                    name: 'einvoice_operator',
                    label: 'E-invoice operator identifier',
                  },
                  {
                    name: 'referring_organization',
                    label: 'Referring organization',
                  },
                  { name: 'referring_vet', label: 'Referring veterinarian' },
                  {
                    name: 'parent_referring_organization',
                    label: 'Linked referring organization',
                  },
                  { name: 'breeder', label: 'Breeder' },
                  { name: 'invoicing_client', label: 'Invoicing client' },
                  { name: 'no_sms', label: 'Disable SMS sending' },
                  { name: 'no_email', label: 'Disable email sending' },
                  { name: 'on_stop', label: 'On stop' },
                  { name: 'archived', label: 'Archived' },
                  // Communication Preferences
                  {
                    name: 'communication_preference_valid_from',
                    label: 'Valid from',
                  },
                  {
                    name: 'communication_preference_consent',
                    label: 'Consent method',
                  },
                  {
                    name: 'communication_preference_privacy_policy_version',
                    label: 'Privacy policy version',
                  },
                  {
                    name: 'communication_preference_1',
                    label: 'Marketing communications',
                  },
                  {
                    name: 'communication_preference_2',
                    label: 'Appointment reminders',
                  },
                  {
                    name: 'communication_preference_3',
                    label: 'Essential service updates',
                  },
                ],
                initials: {
                  state: 'FL',
                  // Communication Preferences initials
                  communication_preference_1: {
                    sms: true,
                    email: true,
                    phone: false,
                    post: false,
                  },
                  communication_preference_2: {
                    sms: true,
                    email: true,
                    phone: true,
                    post: false,
                  },
                  communication_preference_3: {
                    sms: true,
                    email: false,
                    phone: true,
                    post: false,
                  },
                },
                options: {
                  customer_type: [
                    { value: 'CUSTOMER_TYPE_PRIVATE', label: 'Private person' },
                    { value: 'CUSTOMER_TYPE_COMPANY', label: 'Company' },
                    { value: 'CUSTOMER_TYPE_EU_COMPANY', label: 'EU Company' },
                    {
                      value: 'CUSTOMER_TYPE_CHARITABLE_ORG',
                      label: 'Charitable organization',
                    },
                    { value: 'CUSTOMER_UK_CHARITY', label: 'UK charity' },
                  ],
                  communication_preference_consent: [
                    { value: 'FACE_TO_FACE', label: 'Face to face' },
                    { value: 'EMAIL', label: 'Email' },
                    { value: 'TELEPHONE', label: 'Telephone' },
                    { value: 'LEGACY_SYSTEM', label: 'Legacy system' },
                    { value: 'SELF_SERVICE', label: 'Self service' },
                  ],
                },
                help_text: {
                  firstname: 'Legal first name of the client',
                  lastname: 'Legal last name of the client',
                  organization_name: 'Legal name of the organization',
                  id_number:
                    'National ID number used for identification purposes',
                  register_number: 'Company registration number',
                  vat_number: 'VAT identification number',
                  email: 'Primary email address for communication',
                  phone: 'Primary phone number for contact',
                  customer_type: 'Select the type of customer',
                  // Communication Preferences help text with styles
                  communication_preference_1: {
                    description:
                      'We wish to reach out to you to provide you with a range of offerings over your preferred communication preferences.',
                    style: 'SHOW_FULL_TEXT',
                  },
                  communication_preference_2: {
                    description:
                      'Important reminders about upcoming appointments and vaccinations',
                    style: 'SHOW_FULL_TEXT',
                  },
                  communication_preference_3: {
                    description:
                      'Critical service updates and emergency notifications (cannot be disabled)',
                    style: 'SHOW_ON_HOVER',
                  },
                },
              },
            ],
            controls: null,
            regulations: [
              {
                id: 'invoice_country',
                info: 'Controls country-specific behaviors: address field ordering (US/CA format vs European), and special widgets (postcode, tax code calculator, lookup tools)',
                value: 'US',
              },
              {
                id: 'phone_number_prefix',
                info: 'Auto-fills phone number prefix when adding new phone number fields',
                value: '+358',
              },
              {
                id: 'require_full_name_or_organization_name',
                info: 'Either full name (firstname and lastname) or organization name must be filled (higher priority)',
                value: false,
              },
              {
                id: 'require_lastname_or_organization_name',
                info: 'Either lastname or organization name must be filled (lower priority)',
                value: true,
              },
              {
                id: 'required_tags',
                info: 'Dict of tag ID to tag name. Client must have at least one of these tags if not empty.',
                value: {},
              },
              {
                id: 'require_id_or_register_number',
                info: 'If customer type is private person or None, ID number must be provided. If customer type is company, register number must be provided.',
                value: false,
              },
              {
                id: 'check_age',
                info: 'Validates client is 18+ years old if ID number is provided',
                value: false,
              },
              {
                id: 'check_id_format',
                info: 'Validates ID number format if it is provided',
                value: false,
              },
              {
                id: 'soft_check_id_number',
                info: 'Checks ID number and shows warnings if it is not correct, does not block the client creation',
                value: true,
              },
              {
                id: 'use_ratsit_id_number',
                info: 'Enables Ratsit ID number lookup widget',
                value: false,
              },
              {
                id: 'use_ratsit_phone_number',
                info: 'Enables Ratsit phone number lookup widget',
                value: false,
              },
              // Communication Preferences regulations
              {
                id: 'use_communication_preference',
                info: 'Show the Communication Preference section if true',
                value: true,
              },
              {
                id: 'communication_preference_section_guidelines',
                info: 'Guidelines text displayed under the section title',
                value:
                  "To help you take the best care of your pet we'd like to contact you by email, post, text, and phone about services, offers, products and our charity activities.\n\nYou can change your mind at any time here in practice or by unsubscribing from any communications you receive.\n\nAre you happy to be contacted?",
              },
              {
                id: 'default_communication_preference_valid_duration',
                info: 'Default valid duration in months (1-120)',
                value: 24,
              },
            ],
            sections: null,
          },
        },
      },
    }
  })

  server.get(
    '/consultations/:parent_lookup_consultation/ui_configs/:id',
    function (_db, request) {
      return {
        data: {
          type: 'UIConfig',
          id: request.params.id,
          attributes: {
            resource_type: null,
            resource_required: false,
            resource_id: null,
            config: {
              id: 'consultation-tabs',
              forms: null,
              controls: [
                {
                  id: 'internal_remarks',
                  link: null,
                  options: null,
                },
                {
                  id: 'ai_patient_history_summary',
                  link: null,
                  options: null,
                  enabled: true,
                },
              ],
              sections: [
                {
                  id: 'overview',
                  forms: [
                    {
                      id: 'consultation-overview-form',
                      hidden: [],
                      visible: [
                        'started',
                        'admitted_time',
                        'ended',
                        'consultation_type',
                        'patient_type',
                        'complaint',
                        'complaint_type',
                        'ward',
                        'preliminarynote',
                        'reporting_dimension_1',
                        'reporting_dimension_2',
                        'patient_location',
                        'patient_location_2',
                        'supervising_veterinarian',
                        'responsible_nurses',
                        'responsible_surgeons',
                        'referring_organization',
                        'referring_vet',
                        'upper_spend_limit',
                      ],
                      labels: [
                        {
                          name: 'ward',
                          label: 'Service',
                        },
                        {
                          name: 'supervising_veterinarian',
                          label: 'Vet',
                        },
                      ],
                      required: [
                        'started',
                        'admitted_time',
                        'complaint',
                        'complaint_type',
                        'consultation_type',
                        'supervising_veterinarian',
                      ],
                      disabled: [],
                    },
                  ],
                  controls: [
                    {
                      id: 'edit',
                      link: null,
                      options: null,
                    },
                  ],
                },
                {
                  id: 'clinical-notes',
                  forms: null,
                  controls: [
                    {
                      id: 'list',
                      link: null,
                      options: null,
                    },
                    {
                      id: 'edit',
                      link: null,
                      options: null,
                    },
                    {
                      id: 'drafts',
                      link: null,
                      options: null,
                    },
                  ],
                  sections: null,
                },
                {
                  id: 'discharge-instructions',
                  forms: null,
                  controls: [
                    {
                      id: 'ai_summary',
                      link: null,
                      options: null,
                      enabled: true,
                      helper_text:
                        "You've reached your monthly limit of 50 discharge instructions. Your limit will reset on Feb 1, 2026.",
                    },
                    {
                      id: 'email',
                      link: null,
                      options: null,
                    },
                    {
                      id: 'edit',
                      link: null,
                      options: null,
                    },
                    {
                      id: 'edit-reason',
                      link: null,
                      options: null,
                    },
                    {
                      id: 'drafts',
                      link: null,
                      options: null,
                    },
                    {
                      id: 'print',
                      link: null,
                      options: null,
                    },
                  ],
                },
                {
                  id: 'diagnoses',
                  forms: null,
                  controls: [
                    {
                      id: 'previous_diagnoses',
                      link: null,
                      options: null,
                    },
                    {
                      id: 'add_new',
                      link: null,
                      options: null,
                    },
                    {
                      id: 'add_from_previous',
                      link: null,
                      options: null,
                    },
                  ],
                },
                {
                  id: 'diagnostics',
                  forms: null,
                  controls: null,
                  sections: [
                    {
                      id: 'laboratory',
                      forms: null,
                      controls: [
                        {
                          id: 'add_new',
                          link: null,
                          options: null,
                        },
                        {
                          id: 'add_results',
                          link: null,
                          options: null,
                        },
                        {
                          id: 'referral_details_link',
                          link: null,
                          options: null,
                        },
                        {
                          id: 'print',
                          link: null,
                          options: null,
                        },
                        {
                          id: 'analysis_remarks',
                          link: null,
                          options: null,
                        },
                        {
                          id: 'internal_status',
                          link: null,
                          options: null,
                        },
                        {
                          id: 'has_previous_laboratory_referrals',
                          link: null,
                          options: null,
                        },
                      ],
                      sections: null,
                    },
                  ],
                },
                {
                  id: 'treatment-items',
                  forms: null,
                  controls: null,
                  sections: [
                    {
                      id: 'add_items',
                      forms: null,
                      controls: [
                        {
                          id: 'previous_items',
                          link: null,
                          options: null,
                        },
                      ],
                      sections: null,
                    },
                    {
                      id: 'treatment_plans',
                      forms: null,
                      controls: [],
                      sections: null,
                    },
                    {
                      id: 'prescriptions',
                      forms: null,
                      controls: [
                        {
                          id: 'general_presc',
                          link: null,
                          options: null,
                        },
                        {
                          id: 'e_presc',
                          link: null,
                          options: null,
                        },
                      ],
                      sections: null,
                    },
                  ],
                },
                {
                  id: 'estimates',
                  forms: null,
                  controls: [
                    {
                      id: 'edit',
                      link: null,
                      options: null,
                    },
                  ],
                  sections: null,
                },
              ],
            },
          },
        },
      }
    },
  )
}
